import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { api, formatApiError } from "@/lib/api";
import { useWs } from "@/context/WorkspaceContext";
import { Avatar } from "@/components/shared";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ArrowLeft, Loader2, Candy, X, Check } from "lucide-react";

const CONFETTI_COLORS = ["#F59E0B", "#e11d48", "#30BD79", "#C68A4E", "#F3E9DD", "#8B5A2B", "#ffd27a"];

function buildRings(members) {
  const caps = [6, 12, 18, 26];
  const rings = [];
  let idx = 0;
  for (const cap of caps) {
    if (idx >= members.length) break;
    rings.push(members.slice(idx, idx + cap));
    idx += cap;
  }
  if (idx < members.length && rings.length) rings[rings.length - 1] = rings[rings.length - 1].concat(members.slice(idx));
  return rings;
}

export default function SnackWall() {
  const ctx = useWs();
  const navigate = useNavigate();
  const { slug } = useParams();
  const goBack = () => (window.history.length > 1 ? navigate(-1) : navigate(`/${slug}`));
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [dim, setDim] = useState(() => Math.min(typeof window !== "undefined" ? window.innerWidth : 1200, typeof window !== "undefined" ? window.innerHeight : 800));
  const [editing, setEditing] = useState(false);
  const [snackDraft, setSnackDraft] = useState("");
  const [savingSnack, setSavingSnack] = useState(false);

  const load = async () => {
    try { const { data } = await api.get("/snackwall"); setMembers(data.members || data || []); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  useEffect(() => {
    const onResize = () => setDim(Math.min(window.innerWidth, window.innerHeight));
    const onKey = (e) => { if (e.key === "Escape") { if (selected) setSelected(null); else if (editing) setEditing(false); else goBack(); } };
    window.addEventListener("resize", onResize);
    window.addEventListener("keydown", onKey);
    return () => { window.removeEventListener("resize", onResize); window.removeEventListener("keydown", onKey); };
  }, [selected, editing, navigate]);

  const rings = useMemo(() => buildRings(members), [members]);
  const me = useMemo(() => members.find((m) => m.id === ctx.user?.id), [members, ctx.user]);

  const openEdit = () => { setSnackDraft(me?.favourite_snack || ""); setEditing(true); };
  const saveSnack = async () => {
    const snack = snackDraft.trim();
    setSavingSnack(true);
    try {
      await api.put("/profile", { favourite_snack: snack });
      await load();
      ctx.reload?.();
      setEditing(false);
      toast.success(snack ? "Snack saved. Yum." : "Snack cleared");
      const updated = { ...(me || {}), id: ctx.user?.id, name: ctx.user?.name, account: ctx.user?.account, favourite_snack: snack, has_avatar: ctx.user?.has_avatar };
      if (snack) setTimeout(() => setSelected(updated), 250);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSavingSnack(false); }
  };

  const baseR = dim * 0.19;
  const stepR = dim * 0.135;
  const hubSize = Math.max(150, dim * 0.2);

  return (
    <div data-testid="snackwall-page" className="fixed inset-0 z-[60] overflow-hidden" style={{ background: "#180d06" }}>
      {/* warm ambient glow + grain */}
      <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(60% 60% at 50% 50%, rgba(198,138,78,0.22) 0%, rgba(24,13,6,0) 70%)" }} />
      <div className="absolute inset-0 pointer-events-none opacity-[0.05]" style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='120' height='120'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")" }} />

      {/* header */}
      {!selected && !editing && (
      <div className="absolute top-0 inset-x-0 z-[65] flex items-center justify-between px-6 md:px-10 py-5">
        <div className="flex items-center gap-2.5 text-[#F3E9DD]">
          <Candy className="h-5 w-5 text-[#F59E0B]" />
          <div className="leading-tight">
            <p className="font-display text-base font-semibold tracking-tight">SnackWall</p>
            <p className="font-mono-data text-[10px] uppercase tracking-[0.18em] text-[#C68A4E]">The team, orbiting sugar</p>
          </div>
        </div>
        <button data-testid="snackwall-set-snack" onClick={openEdit}
          className="flex items-center gap-2 rounded-full border border-[#C68A4E]/40 bg-white/[0.06] backdrop-blur px-4 py-2 text-sm text-[#F3E9DD] hover:bg-white/[0.12] hover:border-[#F59E0B] transition-colors">
          <Candy className="h-4 w-4 text-[#F59E0B]" /> {me?.favourite_snack ? "Change my snack" : "Set my snack"}
        </button>
      </div>
      )}

      {loading ? (
        <div className="absolute inset-0 flex items-center justify-center text-[#C68A4E]"><Loader2 className="h-6 w-6 animate-spin" /></div>
      ) : (
        <>
          {/* orbit rings */}
          {rings.map((ring, ri) => {
            const r = baseR + ri * stepR;
            const duration = 64 + ri * 18;
            const dir = ri % 2 === 0 ? 360 : -360;
            return (
              <motion.div key={ri} className="absolute rounded-full" aria-hidden
                style={{ left: "50%", top: "50%", width: r * 2, height: r * 2, marginLeft: -r, marginTop: -r }}
                animate={{ rotate: dir }} transition={{ duration, repeat: Infinity, ease: "linear" }}>
                {/* faint orbit path */}
                <div className="absolute inset-0 rounded-full border border-[#C68A4E]/10" />
                {ring.map((m, i) => {
                  const a = (i / ring.length) * Math.PI * 2 - Math.PI / 2;
                  const x = r + r * Math.cos(a);
                  const y = r + r * Math.sin(a);
                  return (
                    <div key={m.id} className="absolute" style={{ left: x, top: y, transform: "translate(-50%,-50%)" }}>
                      <motion.div animate={{ rotate: -dir }} transition={{ duration, repeat: Infinity, ease: "linear" }}>
                        <OrbitAvatar member={m} isMe={m.id === ctx.user?.id} onClick={() => setSelected(m)} />
                      </motion.div>
                    </div>
                  );
                })}
              </motion.div>
            );
          })}

          {/* center chocolate hub with back button */}
          <div className="absolute z-[62]" style={{ left: "50%", top: "50%", transform: "translate(-50%,-50%)" }}>
            <ChocolateHub size={hubSize} onBack={goBack} />
          </div>

          {members.length === 0 && (
            <div className="absolute inset-x-0 bottom-20 text-center text-[#C68A4E] text-sm">No one here yet. Be the first to set a snack.</div>
          )}
        </>
      )}

      {/* burst overlay */}
      <AnimatePresence>
        {selected && <SnackBurst key={selected.id} member={selected} onClose={() => setSelected(null)} onSetSnack={() => { setSelected(null); openEdit(); }} isMe={selected.id === ctx.user?.id} />}
      </AnimatePresence>

      {/* set-my-snack editor */}
      <AnimatePresence>
        {editing && (
          <motion.div className="absolute inset-0 z-[80] flex items-center justify-center bg-black/50 backdrop-blur-sm px-6"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setEditing(false)}>
            <motion.div data-testid="snackwall-snack-editor" onClick={(e) => e.stopPropagation()}
              initial={{ scale: 0.9, y: 20, opacity: 0 }} animate={{ scale: 1, y: 0, opacity: 1 }} exit={{ scale: 0.9, opacity: 0 }}
              className="w-full max-w-sm rounded-3xl bg-[#241610] border border-[#C68A4E]/30 p-6 text-[#F3E9DD] shadow-2xl">
              <div className="flex items-center justify-between mb-1">
                <p className="font-display text-lg font-semibold flex items-center gap-2"><Candy className="h-5 w-5 text-[#F59E0B]" /> Your favourite snack</p>
                <button onClick={() => setEditing(false)} className="text-[#C68A4E] hover:text-white"><X className="h-4 w-4" /></button>
              </div>
              <p className="text-xs text-[#C68A4E] mb-4">Whatever you reach for at 3pm. It shows up on the wall for the whole team.</p>
              <Input data-testid="snackwall-snack-input" value={snackDraft} onChange={(e) => setSnackDraft(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") saveSnack(); }} autoFocus maxLength={60}
                placeholder="e.g. Salt and vinegar chips" className="bg-white/[0.06] border-[#C68A4E]/30 text-[#F3E9DD] placeholder:text-[#8a6b4f]" />
              <div className="flex justify-end gap-2 mt-4">
                <Button variant="ghost" onClick={() => setEditing(false)} className="text-[#C68A4E] hover:text-white hover:bg-white/[0.06] rounded-full">Cancel</Button>
                <Button data-testid="snackwall-snack-save" onClick={saveSnack} disabled={savingSnack}
                  className="bg-[#F59E0B] text-[#241610] hover:bg-[#ffb638] rounded-full font-semibold">
                  {savingSnack ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Check className="h-4 w-4 mr-1.5" /> Save snack</>}
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function OrbitAvatar({ member, isMe, onClick }) {
  const [hover, setHover] = useState(false);
  return (
    <motion.button
      data-testid="snackwall-avatar"
      onClick={onClick}
      onHoverStart={() => setHover(true)} onHoverEnd={() => setHover(false)}
      whileHover={{ scale: 1.22 }} whileTap={{ scale: 0.92 }}
      className="relative block rounded-full"
      style={{ filter: "drop-shadow(0 6px 14px rgba(0,0,0,0.5))" }}>
      <span className={`block rounded-full ${isMe ? "ring-[3px] ring-[#F59E0B]" : "ring-2 ring-white/70"}`}>
        <Avatar name={member.name} size={58} account={member.account} userId={member.id} hasAvatar={member.has_avatar} />
      </span>
      {!member.favourite_snack && <span className="absolute -bottom-1 -right-1 h-4 w-4 rounded-full bg-[#241610] border border-[#C68A4E]/50 flex items-center justify-center"><span className="h-1.5 w-1.5 rounded-full bg-[#C68A4E]" /></span>}
      <AnimatePresence>
        {hover && (
          <motion.span initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
            className="absolute left-1/2 -translate-x-1/2 top-full mt-2 whitespace-nowrap rounded-full bg-[#241610] border border-[#C68A4E]/30 px-2.5 py-1 text-[11px] font-medium text-[#F3E9DD] pointer-events-none">
            {member.name}{isMe ? " (you)" : ""}
          </motion.span>
        )}
      </AnimatePresence>
    </motion.button>
  );
}

function ChocolateHub({ size, onBack }) {
  const cols = 3, rows = 3;
  return (
    <div className="relative" style={{ width: size, height: size }}>
      {/* chocolate bar */}
      <div className="absolute inset-0 rounded-[26px] p-2.5"
        style={{ background: "linear-gradient(145deg,#6b4226,#4a2c1a)", boxShadow: "0 24px 60px rgba(0,0,0,0.6), inset 0 3px 6px rgba(255,220,170,0.35), inset 0 -8px 18px rgba(0,0,0,0.5)" }}>
        <div className="grid h-full w-full gap-1.5" style={{ gridTemplateColumns: `repeat(${cols},1fr)`, gridTemplateRows: `repeat(${rows},1fr)` }}>
          {Array.from({ length: cols * rows }).map((_, i) => (
            <div key={i} className="rounded-[10px]" style={{ background: "linear-gradient(150deg,#6b4226,#3f2515)", boxShadow: "inset 0 2px 3px rgba(255,215,160,0.30), inset 0 -3px 5px rgba(0,0,0,0.5)" }} />
          ))}
        </div>
      </div>
      {/* back to mission control */}
      <button data-testid="snackwall-back" onClick={onBack}
        className="group absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 flex flex-col items-center justify-center rounded-full bg-[#F3E9DD] text-[#241610] shadow-[0_10px_30px_rgba(0,0,0,0.5)] hover:bg-white transition-colors"
        style={{ width: size * 0.52, height: size * 0.52 }}>
        <ArrowLeft className="h-5 w-5 mb-0.5 transition-transform group-hover:-translate-x-0.5" />
        <span className="font-display text-[11px] font-bold leading-tight text-center px-2">Back to<br />Mission Control</span>
      </button>
    </div>
  );
}

function SnackBurst({ member, onClose, onSetSnack, isMe }) {
  const pieces = useRef(Array.from({ length: 22 }).map((_, i) => {
    const a = (i / 22) * Math.PI * 2 + Math.random() * 0.3;
    const d = 180 + Math.random() * 260;
    return { x: Math.cos(a) * d, y: Math.sin(a) * d, c: CONFETTI_COLORS[i % CONFETTI_COLORS.length], s: 8 + Math.random() * 12, r: Math.random() * 360, delay: Math.random() * 0.08 };
  })).current;
  const hasSnack = !!(member.favourite_snack && member.favourite_snack.trim());

  return (
    <motion.div className="absolute inset-0 z-[75] flex items-center justify-center cursor-pointer px-6"
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={onClose} data-testid="snackwall-burst">
      <div className="absolute inset-0 bg-[#180d06]/80 backdrop-blur-md" />
      {/* confetti */}
      {pieces.map((p, i) => (
        <motion.span key={i} className="absolute rounded-[3px]" style={{ backgroundColor: p.c, width: p.s, height: p.s }}
          initial={{ x: 0, y: 0, opacity: 1, rotate: 0, scale: 1 }}
          animate={{ x: p.x, y: p.y, opacity: 0, rotate: p.r, scale: 0.3 }}
          transition={{ duration: 1.1, delay: p.delay, ease: "easeOut" }} />
      ))}
      <motion.div className="relative text-center" initial={{ scale: 0.6, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.8, opacity: 0 }} transition={{ type: "spring", stiffness: 220, damping: 18 }}>
        <div className="flex justify-center mb-5">
          <span className="rounded-full ring-4 ring-[#F59E0B] shadow-2xl"><Avatar name={member.name} size={104} account={member.account} userId={member.id} hasAvatar={member.has_avatar} /></span>
        </div>
        {hasSnack ? (
          <>
            <p className="font-display text-2xl md:text-3xl font-semibold text-[#F3E9DD]">{member.name} loves</p>
            <p data-testid="snackwall-burst-text" className="font-display font-extrabold text-[#F59E0B] leading-[0.95] mt-2 px-4"
              style={{ fontSize: "clamp(2.75rem, 9vw, 7rem)", textShadow: "0 6px 30px rgba(245,158,11,0.35)" }}>
              {member.favourite_snack.toUpperCase()}
            </p>
          </>
        ) : (
          <>
            <p className="font-display text-3xl md:text-4xl font-semibold text-[#F3E9DD]">{member.name}</p>
            <p data-testid="snackwall-burst-text" className="font-display font-bold text-[#C68A4E] mt-3" style={{ fontSize: "clamp(1.5rem, 4vw, 2.75rem)" }}>hasn't picked a snack yet</p>
            {isMe && (
              <Button data-testid="snackwall-burst-setsnack" onClick={(e) => { e.stopPropagation(); onSetSnack(); }} className="mt-6 bg-[#F59E0B] text-[#241610] hover:bg-[#ffb638] rounded-full font-semibold">
                <Candy className="h-4 w-4 mr-1.5" /> Pick your snack
              </Button>
            )}
          </>
        )}
        <p className="mt-8 font-mono-data text-[11px] uppercase tracking-[0.2em] text-[#8a6b4f]">Click anywhere to close</p>
      </motion.div>
    </motion.div>
  );
}
